import { FunctionComponent } from "react";
import { HoverCard as MantineHoverCard } from "@mantine/core";
import classnames from "classnames";
import { Box } from "../box";
import { Group } from "../group";
import { ScrollArea } from "../scroll-area";
import { Text } from "../text";
import styles from "./info-card.module.scss";
import { INFO_CARD_BODY_MAX_HEIGHT } from "./info-card.constants";
import type { IBodyProps, IDropdownProps, IFooterProps, IHeaderProps } from "./info-card.types";

/**
 * The card itself: a light, bordered surface. Hovering never clicks through it unless `allowClicking` is set.
 * @param props.allowClicking Enables pointer events, e.g. for a footer button.
 * @param props.className     Added to the card's own class.
 */
export const Dropdown: FunctionComponent<IDropdownProps> = ({
    children,
    allowClicking = false,
    className,
    ...props
}) => (
    <MantineHoverCard.Dropdown
        // Merged, not replaced: the card's surface and pointer-events rules live on this class.
        className={classnames(styles["info-card-dropdown"], className)}
        data-allow-clicking={allowClicking}
        p={0}
        {...props}
    >
        <Box className={styles["info-card-content"]} bdrs="lg">
            {children}
        </Box>
    </MantineHoverCard.Dropdown>
);

/**
 * The card's title row, above the body. It is a div rather than a paragraph, so it can hold block content such as a
 * title with a subtitle beneath it.
 */
export const Header: FunctionComponent<IHeaderProps> = ({ children }) => (
    <Text component="div" size="sm" variant="medium" className={styles["info-card-header"]} p="lg">
        {children}
    </Text>
);

/**
 * The card's main content: a bordered section with rounded top corners, sitting under the header when there is one.
 * Past the Figma's maximum height it scrolls, which needs the dropdown's `allowClicking` to receive the wheel events.
 */
export const Body: FunctionComponent<IBodyProps> = ({ children }) => (
    <Box
        className={styles["info-card-body"]}
        bd="1px solid var(--mantine-color-border-default)"
        bdrs="var(--mantine-radius-lg) var(--mantine-radius-lg) 0 0"
        mx={-1}
        mb={-1}
    >
        <ScrollArea.Autosize mah={INFO_CARD_BODY_MAX_HEIGHT}>
            <Box p="lg">{children}</Box>
        </ScrollArea.Autosize>
    </Box>
);

/**
 * The card's action row, right-aligned below the body. The body's bottom border is its divider.
 */
export const Footer: FunctionComponent<IFooterProps> = ({ children }) => (
    <Group justify="flex-end" gap="sm" px="lg" py="sm">
        {children}
    </Group>
);
